import * as React from "react";
import {
  Hammer,
  MessageCircle,
  Settings,
  MessageSquareCode,
  BookOpen,
  FlaskConical,
  HandMetal,
  Workflow,
  Box,
  Layout,
  GitBranch,
} from "lucide-react";

import { NavMain } from "@/components/sidebar/nav-main";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
} from "@/components/ui/sidebar";
import { MCPIcon } from "@/components/ui/mcp-icon";
import { AuthUpperArea } from "@/components/auth/auth-upper-area";
import { AppWorkspaceSelector } from "@/components/app-workspace/AppWorkspaceSelector";
import { usePreferencesStore } from "@/stores/preferences/preferences-provider";

// Define sections with their respective items
const navigationSections = [
  {
    id: "connection",
    items: [
      {
        title: "All Servers",
        url: "#servers",
        icon: MCPIcon,
      },
      {
        title: "Playground",
        url: "#chat-v2",
        icon: MessageCircle,
      },
    ],
  },
  {
    id: "primitives",
    items: [
      {
        title: "Widgets",
        url: "#widgets",
        icon: Layout,
      },
      {
        title: "Tools",
        url: "#tools",
        icon: Hammer,
      },
      {
        title: "Flows",
        url: "#flows",
        icon: GitBranch,
      },
      {
        title: "Resources",
        url: "#resources",
        icon: BookOpen,
      },
      {
        title: "Prompts",
        url: "#prompts",
        icon: MessageSquareCode,
      },
    ],
  },
  {
    id: "others",
    items: [
      {
        title: "Widget Preview",
        url: "#widget-preview",
        icon: Layout,
      },
      {
        title: "OAuth Debugger",
        url: "#oauth-flow",
        icon: Workflow,
      },
      {
        title: "Evals",
        url: "#evals",
        icon: FlaskConical,
      },
      {
        title: "MCP Registry",
        url: "#registry",
        icon: Box,
      },
    ],
  },
  {
    id: "settings",
    items: [
      {
        title: "Feedback",
        url: "https://github.com/MCPJam/inspector/issues/new",
        icon: HandMetal,
      },
      {
        title: "Settings",
        url: "#settings",
        icon: Settings,
      },
    ],
  },
];

interface MCPSidebarProps extends React.ComponentProps<typeof Sidebar> {
  onNavigate?: (section: string) => void;
  activeTab?: string;
}

export function MCPSidebar({
  onNavigate,
  activeTab,
  ...props
}: MCPSidebarProps) {
  const themeMode = usePreferencesStore((s) => s.themeMode);

  const handleNavClick = (url: string) => {
    if (onNavigate && url.startsWith("#")) {
      onNavigate(url.slice(1));
    } else {
      window.open(url, "_blank");
    }
  };

  const handleLogoClick = () => {
    // Navigate to home page
    window.location.hash = "";
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader className="flex flex-row items-center gap-2">
        <button
          onClick={handleLogoClick}
          className="cursor-pointer hover:opacity-80 transition-opacity pl-1 shrink-0"
          aria-label="Home"
        >
          <img
            src={
              themeMode === "dark"
                ? "/taias_outline_dark.svg"
                : "/taias_outline_light.svg"
            }
            alt="Taias"
            className="h-6 w-6"
          />
        </button>
        <AppWorkspaceSelector />
      </SidebarHeader>
      <SidebarContent>
        {navigationSections.map((section, sectionIndex) => (
          <React.Fragment key={section.id}>
            <NavMain
              items={section.items.map((item) => ({
                ...item,
                isActive: item.url === `#${activeTab}`,
              }))}
              onItemClick={handleNavClick}
            />
            {/* Add subtle divider between sections (except after the last section) */}
            {sectionIndex < navigationSections.length - 1 && (
              <div className="mx-4 my-2 border-t border-border/50" />
            )}
          </React.Fragment>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <a
          href="/docs"
          className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-sidebar-accent"
        >
          <BookOpen className="h-4 w-4" />
          <span className="group-data-[collapsible=icon]:hidden">Docs</span>
        </a>
        <AuthUpperArea />
      </SidebarFooter>
    </Sidebar>
  );
}
